import type { DeepSignalData, DSCompetitiveInstruments } from '@/api/deep-signal-api';
import type { CompetitorFunnelRow, FunnelStageKey, MeasuredModel, InvStatus } from './types';
import { MODEL_ORDER, MODEL_API_KEY, DOT_COLORS, FUNNEL_STAGES } from './constants';
import { getModelLanes } from './model-lanes';
import { isExcludedFromAnalytics } from '@/app/constants/models';
import { variableRows, cellQuotes, modelRows, insightCards, variableScores } from './fixtures';

export function _scoreColor(s: number): { bg: string; textColor: string } {
  if (s >= 1.30) return { bg: '#00c9c4', textColor: '#fff' };
  if (s >= 0.80) return { bg: '#3ddad7', textColor: '#004d4a' };
  if (s >= 0.50) return { bg: '#80e9e7', textColor: '#005957' };
  if (s >= 0.30) return { bg: '#c0f4f3', textColor: '#006663' };
  return { bg: '#e8fbfb', textColor: '#338c8a' };
}

export function buildVariableRowsFromApi(data: DeepSignalData): Array<(typeof variableRows)[number] & { definition: string }> {
  const lanes = getModelLanes(Object.keys(data.model_breakdown));
  return data.variables.map(v => {
    const cells = lanes.map(lane => {
      const score = data.variable_intelligence.by_model[lane.id]?.[v.id] ?? 0;
      const { bg, textColor } = _scoreColor(score);
      const barFill = Math.round(score * 33);
      return {
        value: score.toFixed(3),
        bg,
        textColor,
        barFill,
        delta: `↑${(2 - score).toFixed(2)}`,
        deltaColor: textColor === '#fff' ? 'rgba(255,255,255,0.75)' : 'rgba(0,77,74,0.7)',
      };
    });
    const { name, def } = variableDisplay(v);
    return {
      name,
      definition: def,
      highlight: false,
      wb: 'W1', wbColor: '#00c9c4', wbBg: '#f0fefe',
      sb: '', sbColor: '#b2b5b9', sbBg: '#fafafa',
      cells,
    };
  });
}

export function _truncateQuote(text: string, maxLen = 220): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen).trim() + '...';
}

export function buildCellQuotesFromApi(data: DeepSignalData): typeof cellQuotes {
  // Build cell quotes keyed by [variable short name][model display name],
  // using the complete per-model-per-variable evidence from cell_evidence.
  // Unlike the curated what_ai_says cards (which are capped and pattern
  // filtered, and only exist for the dedicated "What AI Says" tab), this
  // covers every (model, variable) pair so the drawer never silently
  // omits a model that simply didn't get a pattern card.
  const result: Record<string, Record<string, { quote: string; framing: string; framingColor: string; framingBg: string; source: string; noEvidence?: boolean; watch?: string }>> = {};

  const cellEvidence = data.cell_evidence || {};
  const lanes = getModelLanes(Object.keys(data.model_breakdown));

  for (const v of data.variables) {
    const { name: shortName } = variableDisplay(v);
    result[shortName] = result[shortName] || {};

    for (const lane of lanes) {
      const apiKey = lane.id;
      const score  = data.variable_intelligence.by_model[apiKey]?.[v.id] ?? 0;
      const entry  = cellEvidence[apiKey]?.[v.id] ?? null;

      const framing      = score >= 1.5 ? 'Strong encoding' : score >= 0.5 ? 'Partial encoding' : 'Not indexed';
      const framingColor = score >= 1.5 ? '#00c9c4' : score >= 0.5 ? '#28a9d2' : '#ff6d66';
      const framingBg    = score >= 1.5 ? '#e1fdfd'  : score >= 0.5 ? '#e1f5fb'  : '#ffeaea';

      if (entry) {
        result[shortName][apiKey] = {
          quote:        _truncateQuote(entry.evidence),
          framing,
          framingColor,
          framingBg,
          source:       entry.query ? `From: "${_truncateQuote(entry.query, 100)}"` : '',
        };
      } else {
        result[shortName][apiKey] = {
          quote:        'No evidence found for this model on this attribute.',
          framing:      'No evidence',
          framingColor: '#b2b5b9',
          framingBg:    '#fafafa',
          source:       '',
          noEvidence:   true,
        };
      }
    }
  }
  return result;
}

export function buildModelRowsFromApi(data: DeepSignalData): (typeof modelRows[0] | null)[] {
  const lanes = getModelLanes(Object.keys(data.model_breakdown));
  const allStats = lanes.map(l => data.model_breakdown[l.id]).filter(Boolean);
  if (!allStats.length) return [];
  const maxPrimaryRec = Math.max(...allStats.map(s => s.primary_rec_rate));
  const minMention    = Math.min(...allStats.map(s => s.mention_rate));

  return lanes.map(lane => {
    const stats = data.model_breakdown[lane.id];
    if (!stats) return null;
    const mention    = stats.mention_rate;
    const top5       = stats.top5_rate;
    const primaryRec = stats.primary_rec_rate;
    const numOne     = stats.number_one_rate;
    const conv       = mention > 0 ? primaryRec / mention : 0;
    const recColor   = primaryRec >= 0.25 ? '#00c9c4' : primaryRec >= 0.15 ? '#28a9d2' : '#717680';
    const gap        = Math.round((mention - primaryRec) * 100);

    // Hard rule: never use em dashes in generated copy
    const noEmDash = (s: string) => s.replace(/—/g, '-').replace(/--/g, '-');

    let note: string;
    if (numOne > primaryRec) {
      note = noEmDash(`Lists you frequently but rarely leads with you. List presence is not converting to a primary recommendation.`);
    } else if (primaryRec >= maxPrimaryRec) {
      note = noEmDash(`Best primary rec rate in this set. ${Math.round(conv * 100)}% of mentions become a recommendation. The strongest converter here.`);
    } else if (gap >= 50) {
      note = noEmDash(`High mention, low close. The ${gap}-point drop from mention to primary rec is the largest gap in this set.`);
    } else if (mention <= minMention) {
      note = noEmDash(`Smallest reach in this set. When it does mention you, it converts at ${Math.round(conv * 100)}%.`);
    } else {
      note = noEmDash(`${Math.round(conv * 100)}% of mentions convert to a primary recommendation.`);
    }

    return {
      name:       lane.label,
      dotColor:   DOT_COLORS[lane.label] ?? lane.color,
      mention:    `${(mention    * 100).toFixed(1)}%`,
      top5:       `${(top5       * 100).toFixed(1)}%`,
      primaryRec: `${(primaryRec * 100).toFixed(1)}%`,
      recColor,
      conv:       `${(conv       * 100).toFixed(1)}%`,
      convColor:  recColor,
      note,
      inversion:  numOne > primaryRec,
      rowBg:      'transparent',
    };
  });
}

export function buildInsightCardsFromApi(data: DeepSignalData): typeof insightCards {
  if (!data.what_ai_says.length) return [];
  return data.what_ai_says.map(card => ({
    id:         card.id,
    type:       card.card_type,
    typeColor:  card.type_color,
    typeBg:     card.type_bg,
    accentColor: card.accent_color,
    models:     card.models,
    headline:   card.headline,
    snippet:    card.snippet,
    fullQuote:  card.full_quote,
    keyPhrase:  card.key_phrase,
    source:     card.source,
  }));
}

/**
 * Does this lane count toward the client's BLENDED numbers?
 *
 * Two different questions get confused easily, so they are kept apart here:
 *
 *   "may this lane RENDER at all?"  -> isExcludedFromAnalytics(id), the global
 *      catalog rule. gptweb is a duplicate of the base ChatGPT provider and
 *      never renders in Deep Signal.
 *
 *   "does this lane count toward the BLEND?" -> this function. A lane added to
 *      a client after their baseline renders as its own discrete lane
 *      everywhere, and only stays out of blended numbers.
 *
 * Falls back to the catalog rule when the server did not send the per-client
 * set (older API build, or a cached payload), which is exactly the behaviour
 * this file had before per-client eligibility existed.
 */
function blendsForClient(data: DeepSignalData, id: string): boolean {
  const eligible = data.blend_eligible_models;
  if (!eligible || !eligible.length) return !isExcludedFromAnalytics(id);
  return eligible.includes(id);
}

export function buildFunnelStatsFromApi(data: DeepSignalData): { value: string; label: string; sub: string; raw: number }[] {
  // Blended headline: pooled across the lanes that count FOR THIS CLIENT.
  // Two kinds of lane are excluded and they are not the same thing.
  // Measurement-only lanes (gptweb) never blend for anyone, because they
  // duplicate the base ChatGPT provider. A lane added to this client after
  // their baseline (AI Mode on an existing client, say) does not blend either,
  // because mixing it in would move a headline the client has been reading for
  // months and make a coverage change look like a change in how AI answers.
  // Both render as their own lane elsewhere on this page.
  const models = Object.entries(data.model_breakdown)
    .filter(([id]) => blendsForClient(data, id))
    .map(([, m]) => m);
  if (!models.length) return [];
  // Call-weighted (pooled) average, matching the report / Compare Runs rollup
  // (aggregate_score_rows), which divides total hits by total calls rather than
  // averaging per-model rates. When every model has the same call count the two
  // are identical; when counts differ (a model errored more, or ran a different
  // number of queries) the pooled figure is the correct one — an unweighted mean
  // would let a small, low-volume lane swing the headline by a fraction of a
  // point. Weight by total_responses (the per-model call count in the payload).
  const totalCalls = models.reduce((s, m) => s + m.total_responses, 0);
  const pooled = (key: keyof typeof models[0]) =>
    totalCalls > 0
      ? models.reduce((s, m) => s + (m[key] as number) * m.total_responses, 0) / totalCalls
      : 0;
  const mention    = pooled('mention_rate');
  const top5       = pooled('top5_rate');
  const primaryRec = pooled('primary_rec_rate');
  const numOne     = pooled('number_one_rate');
  return [
    { value: `${(mention    * 100).toFixed(1)}%`, label: 'Appears in response',    sub: 'Mention Rate', raw: mention    },
    { value: `${(top5       * 100).toFixed(1)}%`, label: 'In top 5 named',         sub: 'Top-5 Rate',   raw: top5       },
    { value: `${(numOne     * 100).toFixed(1)}%`, label: 'First brand named',       sub: '#1 on List',   raw: numOne     },
    { value: `${(primaryRec * 100).toFixed(1)}%`, label: 'Primary recommendation',  sub: 'Primary Rec',  raw: primaryRec },
  ];
}

// Same output shape/order as buildFunnelStatsFromApi ([0] mention, [1] top5,
// [2] num1, [3] primary), but built from a run's STORED report headline
// (baseline_report_json.headline) rather than the live model_breakdown. This is
// the exact rollup Compare Runs reads — divided by all calls, over the original
// scored pass — so the hero row's "vs baseline" delta matches the report
// instead of re-deriving the baseline from the live (possibly re-scored) rows.
export function buildFunnelStatsFromHeadline(
  h: { mention_rate: number; top5_rate: number; primary_rec_rate: number; number_one_rate: number },
): { value: string; label: string; sub: string; raw: number }[] {
  const mention    = h.mention_rate ?? 0;
  const top5       = h.top5_rate ?? 0;
  const numOne     = h.number_one_rate ?? 0;
  const primaryRec = h.primary_rec_rate ?? 0;
  return [
    { value: `${(mention    * 100).toFixed(1)}%`, label: 'Appears in response',    sub: 'Mention Rate', raw: mention    },
    { value: `${(top5       * 100).toFixed(1)}%`, label: 'In top 5 named',         sub: 'Top-5 Rate',   raw: top5       },
    { value: `${(numOne     * 100).toFixed(1)}%`, label: 'First brand named',       sub: '#1 on List',   raw: numOne     },
    { value: `${(primaryRec * 100).toFixed(1)}%`, label: 'Primary recommendation',  sub: 'Primary Rec',  raw: primaryRec },
  ];
}

export function buildPerModelFunnelRows(data: DeepSignalData): { name: string; mention: number; top5: number; num1: number; primary: number }[] {
  // Per-model cut for the hero row's breakdown table. #1 on List lives here
  // (kept in the payload, surfaced only in this table). Measurement-only
  // lanes are excluded, same as the blended headline.
  const lanes = getModelLanes(Object.keys(data.model_breakdown).filter(id => !isExcludedFromAnalytics(id)));
  return lanes.map(lane => {
    const m = data.model_breakdown[lane.id];
    return {
      name: lane.label,
      mention: m?.mention_rate ?? 0,
      top5: m?.top5_rate ?? 0,
      num1: m?.number_one_rate ?? 0,
      primary: m?.primary_rec_rate ?? 0,
    };
  });
}

export function buildMeasurementSummary(data: DeepSignalData): string {
  const overall = data.variable_intelligence.overall;
  const vars = data.variables;
  const modelBreakdown = data.model_breakdown;

  const scored = vars.map(v => ({
    name: variableDisplay(v).name,
    score: overall[v.id] ?? 0,
  })).sort((a, b) => b.score - a.score);
  const strongest = scored[0];
  const weakest = scored[scored.length - 1];

  const encodedCount = scored.filter(v => v.score > 0.12).length;
  const unencodedCount = scored.length - encodedCount;

  // Same blend rule as the headline: measurement-only lanes and any lane added
  // after this client's baseline stay out of the blended prose numbers and the
  // "best model" pick, so the prose cannot contradict the number above it.
  const modelEntries = Object.entries(modelBreakdown).filter(([id]) => blendsForClient(data, id));
  const sortedByRec = [...modelEntries].sort((a, b) => b[1].primary_rec_rate - a[1].primary_rec_rate);
  const bestRecModel = sortedByRec[0];
  const MODEL_DISPLAY: Record<string, string> = { openai: 'ChatGPT', claude: 'Claude', gemini: 'Gemini', perplexity: 'Perplexity' };

  const avgMention = modelEntries.reduce((s, [, m]) => s + m.mention_rate, 0) / modelEntries.length;
  const avgPrimary = modelEntries.reduce((s, [, m]) => s + m.primary_rec_rate, 0) / modelEntries.length;
  const mentionPct = Math.round(avgMention * 100);
  const primaryPct = Math.round(avgPrimary * 100);

  const bestRecName = MODEL_DISPLAY[bestRecModel?.[0]] ?? bestRecModel?.[0];
  const bestRecPct = Math.round((bestRecModel?.[1].primary_rec_rate ?? 0) * 100);
  const strongestLabel = strongest.name.charAt(0).toUpperCase() + strongest.name.slice(1);

  let summary = `You show up in ${mentionPct}% of AI responses. You convert to a primary recommendation ${primaryPct}% of the time. `;
  summary += `${strongestLabel} is the one attribute all four models have encoded consistently. `;
  if (unencodedCount > 0) {
    const weakestLabel = weakest.name.charAt(0).toUpperCase() + weakest.name.slice(1);
    summary += `${unencodedCount} of ${scored.length} variables are near-invisible in the AI record right now`;
    if (weakest.name !== strongest.name) {
      summary += `, and ${weakestLabel} has the most room to move. `;
    } else {
      summary += `. `;
    }
  }
  summary += `${bestRecName} is the model most likely to recommend you at ${bestRecPct}%. That number is what shifts when the variables below get encoded.`;

  return summary;
}

export function buildVariableScoresFromApi(data: DeepSignalData): Array<(typeof variableScores)[number] & { definition: string }> {
  const overall = data.variable_intelligence.overall;
  return data.variables.map(v => {
    const score = overall[v.id] ?? 0;
    const { name, def } = variableDisplay(v);
    return { name, definition: def, score, encoded: score >= 0.8, group: score >= 0.8 ? 'encoded' : 'gap' };
  }).sort((a, b) => b.score - a.score);
}

// ── Persona rows from API ────────────────────────────────────────────────────
// Maps structured persona aggregates from API into render-ready row objects.
export function buildPersonaRowsFromApi(data: DeepSignalData): {
  name: string; role: string; decisionFrame: string; accentColor: string;
  mention: string; top5: string; primaryRec: string; primaryRecColor: string;
  bestModel: string; bestModelRate: string; bestModelColor: string;
  variableScores: Record<string, number>;
}[] {
  const lanes = getModelLanes(Object.keys(data.model_breakdown));
  return data.personas.map((p) => {
    const primaryRate = p.primary_rec_rate;
    const primaryRecColor = primaryRate >= 0.25 ? '#00c9c4' : primaryRate >= 0.15 ? '#28a9d2' : '#ff6d66';
    const bestLane = lanes.reduce<(typeof lanes)[number] | null>(
      (best, lane) => {
        if (!best) return lane;
        return (p.per_model[lane.id]?.primary_rec_rate ?? 0) > (p.per_model[best.id]?.primary_rec_rate ?? 0) ? lane : best;
      },
      null,
    );
    return {
      name:           p.name,
      role:           p.role,
      decisionFrame:  p.decisionFrame,
      accentColor:    p.accentColor,
      mention:        `${Math.round(p.mention_rate * 100)}%`,
      top5:           `${Math.round(p.top5_rate * 100)}%`,
      primaryRec:     `${Math.round(primaryRate * 100)}%`,
      primaryRecColor,
      bestModel:      bestLane?.label ?? '',
      bestModelRate:  `${Math.round((bestLane ? p.per_model[bestLane.id]?.primary_rec_rate ?? 0 : 0) * 100)}%`,
      bestModelColor: bestLane ? (DOT_COLORS[bestLane.label] ?? bestLane.color) : '#12b76a',
      variableScores: p.variable_scores,
    };
  });
}

// ── Competitive data from API ────────────────────────────────────────────────
// The competitive tab shows the COMPETITIVE FIELD only, never the client's own
// row. The client's funnel is the hero funnel, measured by the focal scorer
// over all questions; the competitor rows here are the cheaper competitive
// scorer over the neutral-question subset. Those are two different
// measurements, so the client must never appear in this table: a second,
// different number for the same brand's funnel would read as a contradiction.
// The client compares itself against this field using the hero funnel.
//
// The payload's `instruments` block names the source per column ("judged" /
// "benchmark"); null means unmeasured and the column is not rendered at all
// (visibleStages). An older payload without the block renders all columns.
export function buildCompetitiveFromApi(data: DeepSignalData): {
  funnelRows: CompetitorFunnelRow[];
  models: MeasuredModel[];
  visibleStages: FunnelStageKey[];
  instruments: DSCompetitiveInstruments | null;
} {
  const instruments = data.competitive?.instruments ?? null;
  const stageMeasured: Record<FunnelStageKey, boolean> = {
    mentionRate:    instruments === null || instruments.mention != null,
    top5Rate:       instruments === null || instruments.top5    != null,
    primaryRecRate: instruments === null || instruments.primary != null,
  };
  const visibleStages = (['mentionRate', 'top5Rate', 'primaryRecRate'] as FunnelStageKey[])
    .filter(key => stageMeasured[key]);

  // The models this run measured, for the funnel table's model filter. This is
  // the same lane set that fed the retired Per-Model Competitive Presence
  // cards, read from the same competitor per_model payload. Measurement-only
  // lanes (gptweb) are skipped: competitor per-model rates only exist for the
  // named models, so a gptweb option would filter to an empty column.
  const models: MeasuredModel[] = getModelLanes(Object.keys(data.model_breakdown))
    .filter(lane => !isExcludedFromAnalytics(lane.id))
    .map(lane => ({ key: lane.id, label: lane.label }));

  // Competitor rows only. Unmeasured rows (null mentionRate) sort to the bottom.
  const funnelRows: CompetitorFunnelRow[] = (data.competitive?.competitor_brands ?? [])
    .map(c => ({
      brand:          c.name,
      mentionRate:    c.mention_rate     == null ? null : c.mention_rate     * 100,
      top5Rate:       c.top5_rate        == null ? null : c.top5_rate        * 100,
      primaryRecRate: c.primary_rec_rate == null ? null : c.primary_rec_rate * 100,
      perModel: Object.fromEntries(
        models.map(m => {
          const v = c.per_model?.[m.key];
          return [m.key, v == null ? null : v * 100];
        }),
      ),
    }))
    .sort((a, b) => (b.mentionRate ?? -1) - (a.mentionRate ?? -1));

  return { funnelRows, models, visibleStages, instruments };
}

// Plain-language source line for the Competitive tab explainer. Names each
// visible column's instrument so two surfaces showing the same metric under
// different instruments stay distinguishable.
export function competitiveInstrumentNote(instruments: DSCompetitiveInstruments | null): string {
  if (instruments === null) return '';
  if (instruments.mention === 'benchmark') {
    return 'Competitor rates come from this brand\'s measured baseline benchmarks.';
  }
  if (instruments.mention === 'judged' || instruments.top5 === 'judged' || instruments.primary === 'judged') {
    return '';
  }
  return '';
}

// One display contract for a rubric variable: a short human title plus the
// full claim as its definition. Colon-style claims ("Title: definition") keep
// their authored split. Sentence-style claims (no colon) take their title
// from the variable's id key (whole_child_formation -> "Whole child
// formation") and the whole claim becomes the definition, shown on demand.
// This is what keeps full claim sentences from rendering as squished
// multi-line labels next to the charts.
export function variableDisplay(v: { id: string; claim: string }): { name: string; def: string } {
  const claim = v.claim || '';
  if (claim.includes(':')) {
    const [name, ...rest] = claim.split(':');
    return { name: name.trim(), def: rest.join(':').trim() };
  }
  const fromId = (v.id || '').replace(/[_-]+/g, ' ').trim();
  if (fromId) {
    return { name: fromId.charAt(0).toUpperCase() + fromId.slice(1), def: claim };
  }
  return { name: claim, def: '' };
}

// ── Model strategic reads — dynamically computed from API data ────────────────
// Numbers are live from the API. Per-model strategic context (how each model
// sources content, what drives its recommendations) is necessarily model-specific
// knowledge, but every metric interpolated into the copy is computed at runtime.
export function buildModelStrategicReads(data: DeepSignalData): Array<{
  name: string;
  dotColor: string;
  snippet: string;
  strategic: string;
}> {
  // Hard rule: no em dashes in any generated string
  const clean = (s: string) => s.replace(/—/g, '-').replace(/—/g, '-');

  const allStats = MODEL_ORDER
    .map(n => ({ name: n, stats: data.model_breakdown[MODEL_API_KEY[n]] }))
    .filter(m => m.stats);

  const maxPrimary  = Math.max(...allStats.map(m => m.stats.primary_rec_rate));
  const maxMention  = Math.max(...allStats.map(m => m.stats.mention_rate));
  const minMention  = Math.min(...allStats.map(m => m.stats.mention_rate));
  const avgConv     = allStats.reduce((s, m) => {
    const c = m.stats.mention_rate > 0 ? m.stats.primary_rec_rate / m.stats.mention_rate : 0;
    return s + c;
  }, 0) / allStats.length;

  return allStats.map(({ name, stats }) => {
    const mention    = stats.mention_rate;
    const primary    = stats.primary_rec_rate;
    const numOne     = stats.number_one_rate;
    const conv       = mention > 0 ? primary / mention : 0;
    const gap        = Math.round((mention - primary) * 100);
    const mentionPct = Math.round(mention * 100);
    const primaryPct = Math.round(primary * 100);
    const convPct    = Math.round(conv * 100);
    const isInversion     = numOne > primary;
    const isBestPrimary   = primary >= maxPrimary;
    const isHighestMention = mention >= maxMention;
    const isLowestMention  = mention <= minMention;
    const isBelowAvgConv  = conv < avgConv;

    let snippet: string;
    let strategic: string;

    // Compute some cross-model comparisons for richer observations
    const otherPrimaries = allStats.filter(m => m.name !== name).map(m => m.stats.primary_rec_rate);
    const avgOtherPrimary = otherPrimaries.reduce((s, v) => s + v, 0) / otherPrimaries.length;
    const primaryVsAvg = Math.round((primary - avgOtherPrimary) * 100);
    const convRank = [...allStats].sort((a, b) => {
      const ca = a.stats.mention_rate > 0 ? a.stats.primary_rec_rate / a.stats.mention_rate : 0;
      const cb = b.stats.mention_rate > 0 ? b.stats.primary_rec_rate / b.stats.mention_rate : 0;
      return cb - ca;
    }).findIndex(m => m.name === name) + 1;

    if (name === 'ChatGPT') {
      if (isBestPrimary) {
        snippet = clean(`Highest primary rec rate in the set and the most efficient converter of the four models.`);
        strategic = clean(`ChatGPT converts ${convPct}% of its mentions into a primary recommendation, which is the highest conversion rate in this dataset. Its primary rec rate of ${primaryPct}% sits ${primaryVsAvg} points above the average of the other three models. The ${gap}-point gap between mention (${mentionPct}%) and primary rec (${primaryPct}%) is also the smallest gap in the set, meaning ChatGPT is the most likely of the four to follow a mention through to a recommendation. No other model in this dataset comes close to this conversion efficiency.`);
      } else {
        snippet = clean(`${gap}-point gap between mention and primary rec. Strong reach, conversion lags behind.`);
        strategic = clean(`ChatGPT mentions the brand in ${mentionPct}% of responses but converts that to a primary recommendation only ${primaryPct}% of the time. That ${gap}-point gap is larger than average for this set. It ranks ${convRank} out of ${allStats.length} on conversion efficiency. Despite strong mention presence, the brand is not consistently earning the recommendation slot in ChatGPT responses.`);
      }
    } else if (name === 'Claude') {
      if (isHighestMention && isBelowAvgConv) {
        snippet = clean(`Mentions the brand more than any other model. Converts those mentions into recommendations less efficiently than ChatGPT.`);
        strategic = clean(`Claude has the highest mention rate in the dataset at ${mentionPct}%, yet its primary rec rate of ${primaryPct}% produces the largest mention-to-rec gap of any model at ${gap} points. It is the only model where mention rate and primary rec rate diverge by this margin. Claude ranks ${convRank} of ${allStats.length} on conversion efficiency. The brand appears in Claude responses frequently but is not consistently reaching primary recommendation status.`);
      } else {
        snippet = clean(`A ${gap}-point mention-to-rec gap. Mention rate and primary rec rate are not moving together.`);
        strategic = clean(`Claude mentions the brand in ${mentionPct}% of responses but primary rec sits at ${primaryPct}%, a ${gap}-point gap. It ranks ${convRank} of ${allStats.length} on conversion efficiency across the set. The disparity between how often Claude surfaces the brand and how often it recommends it is the defining pattern for this model in this dataset.`);
      }
    } else if (name === 'Gemini') {
      if (isInversion) {
        snippet = clean(`Lowest primary rec rate in the set. Appears in lists more often than it leads with a recommendation.`);
        strategic = clean(`Gemini has a primary rec rate of ${primaryPct}%, tied for the lowest in this dataset, against a ${mentionPct}% mention rate. The ${gap}-point gap between mention and primary rec is notable, and the dataset shows an inversion pattern where Gemini places the brand in ranked lists more often than it assigns it primary recommendation weight. Of the four models, Gemini is the least likely to convert a mention into a primary recommendation at ${convPct}% conversion efficiency.`);
      } else {
        snippet = clean(`${primaryPct}% primary rec and a ${gap}-point gap between mention and recommendation.`);
        strategic = clean(`Gemini mentions the brand in ${mentionPct}% of responses and delivers a primary recommendation ${primaryPct}% of the time, a ${gap}-point gap. It ranks ${convRank} of ${allStats.length} on conversion efficiency at ${convPct}%. Its primary rec rate sits ${Math.abs(primaryVsAvg)} points ${primaryVsAvg >= 0 ? 'above' : 'below'} the average of the other three models in this set.`);
      }
    } else if (name === 'Perplexity') {
      if (isLowestMention) {
        snippet = clean(`Lowest mention rate in the set. When it does mention the brand, it converts at ${convPct}%.`);
        strategic = clean(`Perplexity has the lowest mention rate of the four models at ${mentionPct}%, and a primary rec rate of ${primaryPct}%. Despite the lowest reach, its ${convPct}% conversion rate ranks ${convRank} of ${allStats.length} in the set. That means Perplexity is more selective about when it surfaces the brand, but when it does, the likelihood of a recommendation is not the worst in the dataset. The low mention rate is the primary driver of its overall primary rec count, not its conversion efficiency.`);
      } else {
        snippet = clean(`${mentionPct}% mention rate with a ${convPct}% conversion rate.`);
        strategic = clean(`Perplexity mentions the brand in ${mentionPct}% of responses and converts at ${convPct}%, ranking ${convRank} of ${allStats.length} on conversion efficiency. Its primary rec rate of ${primaryPct}% sits ${Math.abs(primaryVsAvg)} points ${primaryVsAvg >= 0 ? 'above' : 'below'} the average of the other three models.`);
      }
    } else {
      snippet = clean(`${primaryPct}% primary rec across ${mentionPct}% of responses.`);
      strategic = clean(`This model converts ${convPct}% of its mentions into primary recommendations, ranking ${convRank} of ${allStats.length} on conversion efficiency in this dataset. A ${gap}-point gap separates its mention rate from its primary rec rate.`);
    }

    return { name, dotColor: DOT_COLORS[name] ?? '#b2b5b9', snippet, strategic };
  });
}

// Deterministic, computed-from-data callouts — no LLM generation involved, so
// the copy is accurate by construction for any brand or client. Surfaces:
//  1) the brand that retains the largest share of its mentions through to a
//     primary recommendation (primaryRecRate ÷ mentionRate), and
//  2) the single steepest point-drop between any two adjacent funnel stages.
export function getFunnelInsights(data: CompetitorFunnelRow[]) {
  let bestConversion = { brand: '', rate: -Infinity };
  let steepestDrop = { brand: '', fromLabel: '', toLabel: '', drop: -Infinity };

  for (const row of data) {
    // Skip unmeasured rows (null rates) so insights reflect real data only.
    if (row.mentionRate == null || row.primaryRecRate == null) continue;
    // A zero mention rate has no meaningful conversion (division by zero).
    if (row.mentionRate > 0) {
      const conversion = (row.primaryRecRate / row.mentionRate) * 100;
      if (conversion > bestConversion.rate) {
        bestConversion = { brand: row.brand, rate: conversion };
      }
    }
    for (let i = 0; i < FUNNEL_STAGES.length - 1; i++) {
      const from = FUNNEL_STAGES[i];
      const to = FUNNEL_STAGES[i + 1];
      const fromVal = row[from.key];
      const toVal = row[to.key];
      if (fromVal == null || toVal == null) continue;
      const drop = fromVal - toVal;
      if (drop > steepestDrop.drop) {
        steepestDrop = { brand: row.brand, fromLabel: from.label, toLabel: to.label, drop };
      }
    }
  }

  return { bestConversion, steepestDrop };
}

export function blueprintStatusKey(customerId: number, invId: string) {
  return `bp_status_${customerId}_${invId}`;
}

export function loadStatus(customerId: number, invId: string): InvStatus {
  try {
    const v = localStorage.getItem(blueprintStatusKey(customerId, invId));
    if (v === 'in_progress' || v === 'complete') return v;
  } catch {}
  return 'not_started';
}

export function saveStatus(customerId: number, invId: string, status: InvStatus) {
  try { localStorage.setItem(blueprintStatusKey(customerId, invId), status); } catch {}
}
